import { createStore, StoreApi } from 'zustand/vanilla';
import { ArenaStoreState } from './arenaStoreTypes';
import {
  computeAuthoritativeBaseState,
  reconcileOptimisticState,
  computeEffectivePrivateHand,
  computeEffectiveGameState,
  OptimisticActionState,
  PlayerIdentityContext,
} from '../state/arenaStateComputation';
import { createTableSlice, initialTableState } from './slices/createTableSlice';
import { createOptimisticSlice, initialOptimisticState } from './slices/createOptimisticSlice';
import { createSyncSlice, initialSyncState } from './slices/createSyncSlice';
import { createVisualSlice, initialVisualState } from './slices/createVisualSlice';
import { createLockSlice, initialLockState } from './slices/createLockSlice';

export type ArenaStoreApi = StoreApi<ArenaStoreState>;

export const getInitialArenaStoreState = (tableId: string | null = null) => ({
  ...initialTableState,
  tableId,
  ...initialOptimisticState,
  ...initialSyncState,
  ...initialVisualState,
  ...initialLockState,
});

/**
 * Factory creating an isolated vanilla Zustand store for an Arena table session.
 */
export const createArenaStore = (initialTableId?: string | null): ArenaStoreApi => {
  const initialStoreState = getInitialArenaStoreState(initialTableId ?? null);

  return createStore<ArenaStoreState>((set, get) => {
    /**
     * Synchronous recompute helper.
     * Derives authoritativeBaseState, reconciles optimistic state, and derives effectiveGameState & effectivePrivateHand.
     */
    const triggerRecompute = () => {
      const state = get();

      // 1. Authoritative base state
      const authoritativeBaseState = computeAuthoritativeBaseState(state.gameState);

      // 2. Reconcile optimistic actions against current authoritative base state
      const currentOptimistic: OptimisticActionState = {
        optimisticSeat: state.optimisticSeat,
        optimisticReady: state.optimisticReady,
        optimisticBid: state.optimisticBid,
        optimisticTrump: state.optimisticTrump,
        optimisticDiscards: state.optimisticDiscards,
        optimisticPlay: state.optimisticPlay,
        optimisticGameExtension: state.optimisticGameExtension,
      };

      const reconciledOptimistic = reconcileOptimisticState(
        currentOptimistic,
        authoritativeBaseState ?? state.gameState,
        state.mySeat,
        state.authoritativeMySeat,
        state.gameState?.seats
      );

      // 3. Compute effective game state
      const identity: PlayerIdentityContext = {
        myPlayerId: state.myPlayerId,
        myName: state.myName,
        myPhotoUrl: state.myPhotoUrl,
        isTableCreatorOrHost: state.isTableCreatorOrHost,
      };

      const effectiveGameState = computeEffectiveGameState(
        authoritativeBaseState ?? state.gameState,
        reconciledOptimistic,
        identity,
        state.mySeat
      );

      // 4. Compute effective private hand (hand must belong to the active table)
      const isHandForActiveTable =
        !state.privateHandTableId || !state.tableId || state.privateHandTableId === state.tableId;
      const activePrivateHand = isHandForActiveTable ? state.myPrivateHand : [];

      const effectivePrivateHand = computeEffectivePrivateHand(
        activePrivateHand,
        authoritativeBaseState ?? state.gameState,
        reconciledOptimistic,
        state.mySeat,
        state.gameState
      );

      set({
        authoritativeBaseState,
        effectiveGameState,
        effectivePrivateHand,
        ...reconciledOptimistic,
      });
    };

    return {
      ...initialStoreState,

      // Composed Slices
      ...createTableSlice(set as any, get, triggerRecompute, initialTableId ?? null),
      ...createOptimisticSlice(set as any, get, triggerRecompute),
      ...createSyncSlice(set as any, get),
      ...createVisualSlice(set as any, get),
      ...createLockSlice(set as any, get),

      // Store-level Reset
      resetArenaStore: () => set(getInitialArenaStoreState(initialTableId ?? null)),
    };
  });
};
